import { useEffect, useState } from "react";
import { App, Form, Input, Modal } from "antd";
import { useTranslation } from "react-i18next";
import errorCodes from "@/apis/error";
import { updatePlaceLocation } from "@/apis/equipment";
import { Equipment } from "@/types/equipment";

/** 设备放置地点表单入参。 */
interface EquipmentPlaceLocationFormProps {
  open: boolean;
  equipment: Equipment;
  onCancel: () => void;
  onSaved: (equipment: Equipment) => void;
}

/** 将空白地点统一转换为接口所需的空值。 */
const normalizePlaceLocation = (value: string) => value.trim() || null;

/** 编辑并保存设备的放置地点。 */
const EquipmentPlaceLocationForm = ({
  open,
  equipment,
  onCancel,
  onSaved,
}: EquipmentPlaceLocationFormProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [placeLocation, setPlaceLocation] = useState("");
  const [saving, setSaving] = useState(false);

  // 每次打开弹窗时回填当前地点，并重置提交状态。
  useEffect(() => {
    if (open) {
      setPlaceLocation(equipment.placeLocation ?? "");
      setSaving(false);
    }
  }, [equipment.id, equipment.placeLocation, open]);

  // 地点未变化时直接关闭，否则提交标准化后的值。
  const handleSave = async () => {
    if (saving) return;

    const nextPlaceLocation = normalizePlaceLocation(placeLocation);
    const currentPlaceLocation = normalizePlaceLocation(
      equipment.placeLocation ?? "",
    );
    if (nextPlaceLocation === currentPlaceLocation) {
      onCancel();
      return;
    }

    setSaving(true);
    try {
      const updatedEquipment = await updatePlaceLocation(
        equipment.id,
        nextPlaceLocation,
      );
      notification.success({
        message: t("equipment.placeLocationUpdateSucceeded"),
      });
      onSaved(updatedEquipment);
    } catch (error) {
      notification.error({
        message: t("equipment.placeLocationUpdateFailed"),
        description: errorCodes[Number(error)] ?? String(error),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("equipment.editPlaceLocation")}
      open={open}
      okText={t("common.confirm")}
      cancelText={t("common.cancel")}
      confirmLoading={saving}
      destroyOnClose
      width={480}
      onOk={handleSave}
      onCancel={onCancel}
    >
      <Form layout="vertical">
        <Form.Item
          label={t("equipment.placeLocation")}
          style={{ marginBottom: 0 }}
        >
          <Input
            value={placeLocation}
            allowClear
            autoFocus
            disabled={saving}
            placeholder={t("equipment.placeLocation")}
            onChange={event => setPlaceLocation(event.target.value)}
            onPressEnter={() => void handleSave()}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EquipmentPlaceLocationForm;
